import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, Briefcase, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';

export default function FAQs() {
  const [expandedJobSeeker, setExpandedJobSeeker] = useState(null);
  const [expandedEmployer, setExpandedEmployer] = useState(null);

  const jobSeekerFAQs = [
    {
      question: 'How do I apply for a job through Talent Link?',
      answer: 'Browse our current opportunities on the Job Seekers page and submit your CV via our application form.'
    },
    {
      question: 'Can I apply for multiple positions?',
      answer: 'Yes, you can submit applications for any role that matches your skills and experience.'
    },
    {
      question: 'Does Talent Link charge job seekers for placements?',
      answer: 'No. Our services for job seekers are completely free.'
    },
    {
      question: 'How long does it take to hear back after applying?',
      answer: 'Typically, candidates are contacted within 7–14 business days if shortlisted. The client determines the timeframes after submission of CVs.'
    }
  ];

  const employerFAQs = [
    {
      question: 'How do I request talent for my organisation?',
      answer: 'Fill out our Employer Request Form on the Employers page. Our team will contact you to discuss your requirements. You can also send an email to info@talentlinkemploymentagency.com'
    },
    {
      question: 'What sectors does Talent Link serve?',
      answer: 'Talent Link is licenced to recruit locally and internationally across all sectors i.e. Mining, Agriculture, Energy, Construction, Industrial, Banking, NGO, Parastatals and Professional Services.'
    },
    {
      question: 'How does Talent Link ensure quality candidates?',
      answer: 'We perform rigorous screening, reference checks, and skills assessments to ensure candidates meet your requirements.'
    },
    {
      question: 'Can Talent Link support temporary or contract staffing?',
      answer: 'Yes, we provide flexible workforce solutions, including contract and project-based staffing.'
    },
    {
      question: 'How long does it take to fill a role?',
      answer: 'Depending on the role\'s complexity and level, placements typically range from 2–6 weeks.'
    },
    {
      question: 'Are you licenced to recruit for foreign companies based outside Zimbabwean borders?',
      answer: 'Yes. We are partnering the ministry of Labour to ensure a dignified foreign recruitment for the local job seekers.'
    },
    {
      question: 'Do you charge employers to facilitate recruitment of students on attachment?',
      answer: 'No. It\'s part our cooperate social responsibility to help students obtain hands on experience.'
    }
  ];

  const toggleJobSeekerFAQ = (index) => {
    setExpandedJobSeeker(expandedJobSeeker === index ? null : index);
  };

  const toggleEmployerFAQ = (index) => {
    setExpandedEmployer(expandedEmployer === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Frequently Asked Questions – Talent Link Employment Agency</title>
        <meta name="description" content="Answers to common questions for job seekers and employers regarding recruitment, placement, and HR services in Africa." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <HelpCircle className="mx-auto mb-4 text-blue-200" size={64} />
          <h1 className="text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-blue-100">
            Answers to common questions for job seekers and employers
          </p>
        </div>
      </section>

      {/* FAQs Content */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Job Seekers Section */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Users className="text-green-600" size={40} />
              <h2 className="text-3xl font-bold text-gray-800">For Job Seekers</h2>
            </div>
            <div className="space-y-4">
              {jobSeekerFAQs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition"
                >
                  <button
                    onClick={() => toggleJobSeekerFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                  >
                    <span className="font-semibold text-lg text-gray-800 pr-4">
                      {faq.question}
                    </span>
                    {expandedJobSeeker === index ? (
                      <ChevronUp className="text-green-600 flex-shrink-0" size={24} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={24} />
                    )}
                  </button>
                  {expandedJobSeeker === index && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Employers Section */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <Briefcase className="text-blue-600" size={40} />
              <h2 className="text-3xl font-bold text-gray-800">For Employers</h2>
            </div>
            <div className="space-y-4">
              {employerFAQs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition"
                >
                  <button
                    onClick={() => toggleEmployerFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                  >
                    <span className="font-semibold text-lg text-gray-800 pr-4">
                      {faq.question}
                    </span>
                    {expandedEmployer === index ? (
                      <ChevronUp className="text-blue-600 flex-shrink-0" size={24} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={24} />
                    )}
                  </button>
                  {expandedEmployer === index && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-10 text-center text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
            <p className="text-lg mb-6 text-blue-100">
              Our team is here to help. Get in touch with us today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="mailto:info@talentlinkemploymentagency.com"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition shadow-lg"
              >
                Email Us
              </a>
              <a 
                href="tel:+263772155436"
                className="inline-block bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition border-2 border-white shadow-lg"
              >
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
